import React, { useEffect, useState } from "react";
import { AppShell } from "../../components/Sidebar";
import { api } from "../../lib/api";
import { Plus, School, Pencil, Trash2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function AdminSchools() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", address: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/schools").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (sc) => { setEditing(sc); setForm({ code: sc.code, name: sc.name, address: sc.address || "" }); setOpen(true); };
  const startNew  = () => { setEditing(null); setForm({ code: "", name: "", address: "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/schools/${editing.id}`, { name: form.name, address: form.address });
        toast.success("School updated");
      } else {
        await api.post("/admin/schools", form);
        toast.success("School added");
      }
      setOpen(false); setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/schools/${confirmDel.id}`);
      toast.success("School deleted");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cannot delete school");
    }
  };

  return (
    <AppShell role="admin">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Schools</h1>
          <p className="mt-1 text-slate-500">Onboard and manage schools using Gyan Dhara.</p>
        </div>
        <button onClick={startNew} data-testid="add-school-btn" className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700">
          <Plus size={16} /> Add School
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="py-3 pr-4">#</th>
              <th className="py-3 pr-4">Code</th>
              <th className="py-3 pr-4">Name</th>
              <th className="py-3 pr-4">Address</th>
              <th className="py-3 pr-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((sc, i) => (
              <tr key={sc.id} className="border-b border-slate-50">
                <td className="py-3 pr-4 text-slate-500">{i + 1}</td>
                <td className="py-3 pr-4"><span className="rounded-md bg-violet-50 px-2 py-0.5 font-mono text-xs font-semibold text-violet-700">{sc.code}</span></td>
                <td className="py-3 pr-4 font-medium text-slate-900">{sc.name}</td>
                <td className="py-3 pr-4 text-slate-600">{sc.address || "—"}</td>
                <td className="py-3 pr-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => startEdit(sc)} data-testid={`edit-school-${sc.id}`} className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"><Pencil size={12} /> Edit</button>
                    <button onClick={() => setConfirmDel(sc)} data-testid={`delete-school-${sc.id}`} className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100"><Trash2 size={12} /> Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="py-12 text-center text-sm text-slate-500">No schools yet.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm" data-testid="school-modal">
          <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-600"><School size={18} /></div>
                <h3 className="text-xl font-bold text-slate-900">{editing ? "Edit School" : "Add School"}</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="mt-5 space-y-4">
              <Field label="School Code (2-3 letters)">
                <input data-testid="school-code" required maxLength={4} disabled={!!editing} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={`${inp} ${editing ? "bg-slate-100" : ""}`} placeholder="DP" />
                {editing && <span className="mt-1 block text-xs text-slate-500">Code is read-only — it's part of every username.</span>}
              </Field>
              <Field label="School Name">
                <input data-testid="school-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} placeholder="Sunrise Public School" />
              </Field>
              <Field label="Address">
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp} placeholder="MG Road, Bengaluru" />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button data-testid="submit-school" disabled={saving} className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60">{saving ? "Saving…" : editing ? "Save Changes" : "Add School"}</button>
            </div>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="confirm-delete-school">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 text-rose-600"><AlertTriangle size={18} /></div>
              <h3 className="text-xl font-bold text-slate-900">Delete "{confirmDel.name}"?</h3>
            </div>
            <p className="mt-3 text-sm text-slate-600">This will fail if the school still has any teacher, student or parent. Delete those first, then retry.</p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setConfirmDel(null)} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={doDelete} data-testid="confirm-delete-school-confirm" className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-rose-700">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const inp = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";
const Field = ({ label, children }) => (<label className="block"><span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>{children}</label>);
